import React, { useEffect, useState } from "react";
import { Card, Grid, TextField, InputAdornment } from "@mui/material";
import { SearchIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { knowledgeActions } from "store/knowledge-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import { fetchAllDocuments, downloadDocument } from "app/services/knowledge.service";
import { DocumentDataGrid } from "./DocumentDataGrid";

export const KnowledgeMemberView = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const documents = useSelector((state) => state.knowledge.documents);

  const loadDocuments = () => {
    setIsLoading(true);
    fetchAllDocuments()
      .then((data) => dispatch(knowledgeActions.setDocuments(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (doc) => {
    try {
      const blob = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || `${doc.documentTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  const publishedDocs = documents.filter((d) => d.status === "PUBLISHED");

  const filteredDocs = publishedDocs.filter((doc) => {
    if (!searchQuery) return true;
    return (
      doc.documentTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const sopCount = publishedDocs.filter((d) => d.category === "SOP").length;
  const policyCount = publishedDocs.filter((d) => d.category === "POLICY").length;
  const guideCount = publishedDocs.filter((d) => d.category === "GUIDE").length;

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">Available</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{publishedDocs.length}</MDTypography>
              <MDTypography variant="caption">Total Documents</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">SOPs</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{sopCount}</MDTypography>
              <MDTypography variant="caption">Procedures</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Policies</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{policyCount}</MDTypography>
              <MDTypography variant="caption">Company Policies</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="secondary">Guides</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{guideCount}</MDTypography>
              <MDTypography variant="caption">User Guides</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Documents List */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h5" fontWeight="medium">
                  Knowledge Base
                </MDTypography>
                <TextField
                  size="small"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 280 }}
                />
              </MDBox>
              <MDTypography variant="body2" color="text" mb={2}>
                Browse and download organizational documents, SOPs, and resources.
              </MDTypography>
            </MDBox>
            <DocumentDataGrid
              documents={filteredDocs}
              isLoading={isLoading}
              onDownload={handleDownload}
              showActions={false}
            />
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

